import { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';

/**
 * Capability flags for this tenant. Source of truth is the superadmin-managed
 * `tenant-config.tenants.features`, surfaced by the backend at
 * GET /api/settings/features (which falls back to deployment env). Lushin runs
 * post-call-only, so both default to false until live/training is enabled.
 *
 * Fetched once and cached at module scope so every consumer shares one request.
 */
export interface FeatureFlags {
  liveIntelligence: boolean;
  training: boolean;
  /**
   * The live discovery-checklist coaching. Deliberately separate from
   * `liveIntelligence` — that flag also controls objection/FAQ detection,
   * landmines, competitors, KB generation and bulk upload, none of which
   * Lushin wants back just to ship the checklist.
   */
  livePlaybook: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  liveIntelligence: false,
  training: false,
  livePlaybook: false,
};

let cached: FeatureFlags | null = null;
let inflight: Promise<FeatureFlags> | null = null;

async function fetchFeatures(): Promise<FeatureFlags> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res: any = await apiService.get('/settings/features');
      const features = res?.data?.features ?? res?.features ?? {};
      cached = {
        liveIntelligence: !!features.liveIntelligence,
        training: !!features.training,
        livePlaybook: !!features.livePlaybook,
      };
    } catch {
      cached = { ...DEFAULT_FLAGS };
    } finally {
      inflight = null;
    }
    return cached as FeatureFlags;
  })();
  return inflight;
}

/**
 * Returns the resolved feature flags plus a `loading` flag. While the first
 * request is in flight, flags default to false (the safe, post-call-only state).
 */
export function useFeatures(): FeatureFlags & { loading: boolean } {
  const [flags, setFlags] = useState<FeatureFlags>(cached ?? DEFAULT_FLAGS);
  const [loading, setLoading] = useState<boolean>(cached === null);

  useEffect(() => {
    let mounted = true;
    if (cached) {
      setFlags(cached);
      setLoading(false);
      return;
    }
    fetchFeatures().then((f) => {
      if (mounted) {
        setFlags(f);
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  return { ...flags, loading };
}
